<template>
  <div class="catchup-container">
    <div class="catchup-header">
      <div class="catchup-header__info">
        <h1 class="h5 grey-900">
          {{ $t('Catch up') }}
        </h1>
        <span class="text-sm grey-500"> {{ unreadCount }} {{ $tc('unread notifications') }} </span>
      </div>
      <Button type="ghost" :loading="loading" size="small" color="neutral" @click="markAllAsRead">
        {{ $t('Mark all as read') }}
      </Button>
    </div>
    <div
      v-infinite-scroll="uploadMore"
      infinite-scroll-distance="200"
      infinite-scroll-disabled="busy"
      class="catchup-content spacey-8"
      :class="{ 'catchup-select': !!selectedNotifications.length }"
    >
      <Notification
        v-for="notification in notifications"
        :key="notification.id"
        :notification="notification"
        :is-selected="selectedNotifications.includes(notification.id)"
        @check="handleNotificationSelect(notification.id, $event)"
      />
      <NoNotifications v-if="!notifications.length" />
    </div>
    <MarkAsRead
      v-if="!!selectedNotifications.length"
      :selected-count="selectedNotifications.length"
      @mark="markSelectedAsRead"
    />
  </div>
</template>

<script>
  import Button from '@/components/ui/Button';
  import NoNotifications from '@/containers/catch-up/NoNotifications';
  import Notification from '@/containers/catch-up/Notification';
  import factory from '@/utils/api/factory';
  import MarkAsRead from '@/containers/catch-up/MarkAsRead';
  import removeItem from '@/utils/core/remove-item';
  import infiniteScroll from 'vue-infinite-scroll';

  const LIMIT = 10;
  const OFFSET = 0;

  export default {
    name: 'CatchUpModal',
    directives: { infiniteScroll },
    components: {
      Button,
      NoNotifications,
      Notification,
      MarkAsRead,
    },
    data() {
      return {
        notifications: [],
        loading: false,
        selectedNotifications: [],
        canUploadMore: true, // to avoid double loading when scrolling
        isNextUrl: null,
        offset: OFFSET,
      };
    },
    props: {
      unreadCount: {
        type: Number,
        required: true,
      },
    },
    watch: {
      unreadCount: {
        immediate: true,
        handler(newCount, oldCount) {
          if (oldCount && newCount > oldCount) {
            // Loading new notifications without overwriting all of them
            this.uploadNewNotifications(newCount - oldCount);
          } else {
            this.uploadNotifications();
          }
        },
      },
    },
    methods: {
      async markAllAsRead() {
        this.loading = true;
        try {
          await factory('notifications').postInPath('read_all');
        } catch (e) {
          console.error(e);
        }
        this.loading = false;
      },
      handleNotificationSelect(id, isSelected) {
        if (isSelected) {
          this.selectedNotifications.push(id);
        } else removeItem(this.selectedNotifications, id);
      },
      async markSelectedAsRead() {
        this.loading = true;
        try {
          await factory('notifications').postInPath('read_all', {
            ids: this.selectedNotifications,
          });
          this.selectedNotifications = [];
        } catch (e) {
          console.error(e);
        }
        this.loading = false;
      },
      async uploadNotifications() {
        try {
          let { data } = await factory('notifications').index({
            limit: LIMIT,
            offset: OFFSET,
          });
          this.notifications = data.results;
          this.isNextUrl = !!data.next;
        } catch (e) {
          console.error(e);
        }
      },
      async uploadNewNotifications(limit = 0) {
        try {
          this.offset = this.offset + limit;
          let { data } = await factory('notifications').index({
            limit,
            offset: OFFSET,
          });
          this.notifications = [...data.results, ...this.notifications];
        } catch (e) {
          console.error(e);
        }
      },
      async uploadMore() {
        if (this.isNextUrl && this.canUploadMore) {
          try {
            this.canUploadMore = false;
            this.offset = this.offset + LIMIT;
            let { data } = await factory('notifications').index({
              limit: LIMIT,
              offset: this.offset,
            });
            this.notifications = [...this.notifications, ...data.results];
            this.isNextUrl = !!data.next;
            this.canUploadMore = true;
          } catch (e) {
            console.error(e);
          }
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .catchup-container {
    max-width: 660px;
    min-width: 660px;
    max-height: calc(100vh - 100px);
    height: 500px;
    background: #ffffff;
    box-shadow: 0px 4px 8px rgba(31, 45, 63, 0.03), 0px 8px 32px rgba(31, 45, 63, 0.1);
    border-radius: 8px;
    overflow: hidden;
  }

  .catchup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: inset 0px -1px 0px #e8ebf0;
    padding: 12px 20px;
    width: 100%;
    background-color: white;
  }

  .catchup-content {
    position: relative;
    padding: 20px;
    overflow: auto;
    height: calc(100% - 72px);
  }

  .catchup-select {
    margin-bottom: 108px;
  }

  .catchup-notifications-count {
    height: 20px;
  }
</style>
